import { isHtml, stripHtml } from '@/lib/richContent';
import { cn } from '@/lib/utils';

/**
 * <RichContent /> — safely render a CMS field that may contain WYSIWYG HTML
 * or plain text. Falls back to a <p> when the input is a plain string.
 *
 * Props:
 *  - html      the field value (may include <p>, <ul>, <a>, etc.)
 *  - as        wrapper tag (default: 'div')
 *  - className extra classes for the wrapper
 *  - prose     bool (default true) — apply prose styling
 *  - inline    bool (default false) — strip block styling; useful for one-liners
 */
export const RichContent = ({
    html,
    as: Tag = 'div',
    className = '',
    prose = true,
    inline = false,
    'data-testid': testId,
    ...rest
}) => {
    if (!html) return null;

    // Plain-text input — render as a paragraph.
    if (!isHtml(html)) {
        if (inline) {
            return (
                <span className={className} data-testid={testId} {...rest}>
                    {html}
                </span>
            );
        }
        return (
            <p className={className} data-testid={testId} {...rest}>
                {html}
            </p>
        );
    }

    // Inline mode: strip block tags → return raw string in a span.
    if (inline) {
        return (
            <span className={className} data-testid={testId} {...rest}>
                {stripHtml(html)}
            </span>
        );
    }

    // Full HTML mode.
    return (
        <Tag
            className={cn(
                prose &&
                    'prose prose-sm sm:prose max-w-none [&_p]:leading-relaxed [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mt-1 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:font-display [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-2 [&_strong]:text-foreground [&_a]:text-[hsl(var(--primary))] [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-[hsl(var(--accent))] [&_blockquote]:pl-4 [&_blockquote]:italic',
                className
            )}
            data-testid={testId}
            dangerouslySetInnerHTML={{ __html: html }}
            {...rest}
        />
    );
};

export default RichContent;
